import { TitanSprite } from "@lib/titan2/dist/game/control/TitanSprite";
import { TitanText } from "@lib/titan2/dist/game/control/TitanText";
import { OrientationType } from "@lib/titan2/dist/game/renderer/constant/RendererConstants";
import { LoaderEvent } from "@lib/titan2/dist/loader/event/LoaderEvent";
import { Logger } from "@lib/titan2/dist/util/log/Logger";
import { DestroyUtil } from "@lib/titan2/dist/util/view/DestroyUtil";
import { GraphicsUtil } from "@lib/titan2/dist/util/view/GraphicsUtil";
import { gsap } from "gsap";
import { PixiPlugin, MotionPathPlugin } from "gsap/all";
import { GameLang } from "../lang/GameLang";
import { Locale } from "@lib/titan2/dist/game/manager/LanguageManager";
import { AbstractGameView } from "@lib/titan2-slingo/dist/game/view/abstract/display/AbstractGameView";
import { FactoryManager } from "@lib/titan2/dist/game/factory/FactoryManager";
import { Point } from "pixi.js";
import { LoaderDialog } from "@lib/titan2-slingo/dist/game/view/abstract/display/LoaderDialog";
gsap.registerPlugin(PixiPlugin);
gsap.registerPlugin(MotionPathPlugin);
/**
 * GameLoaderDialog.ts
 */
export class GameLoaderDialog extends LoaderDialog {
    create() {
        this.createModel();
        Logger.addObjectRef(this, "LoaderDialog");
        this.createBackground();
        this.createOverlay();
        this.createProgressBar();
        this.createProgressBarMask();
        this.createVersionField();
        this.createProgressField();
        this.createLogo();
        this.createPageHolder();
        this.createPreviousButton();
        this.createNextButton();
        this.createPaging();
        this.createPages();
        this.createSwipe();
        this.createPlayButton();
        this.updatePage();
        this.copyright = TitanText.createField(this, this.landscapeProperties.copyrightProperties, this.portraitProperties?.copyrightProperties);
        this.copyright.name = "copyright";
        this.copyright.setText(`${this.langManager.getLang(GameLang.LEGAL_GMR)}`, true);
        this.createMasks();
    }
    postCreate() {
        super.postCreate();
    }
    destroy() {
        super.destroy();
        this.landscapeMask = null;
        this.portraitMask = null;
        this.progressBarForeground = null;
        this.progressBarTween = null;
        DestroyUtil.destroy(this.infoFieldSlingo);
        this.infoFieldSlingo = null;
        DestroyUtil.destroy(this.infoFieldWin);
        this.infoFieldWin = null;
        DestroyUtil.destroy(this.infoFieldSlot);
        this.infoFieldSlot = null;
        DestroyUtil.destroy(this.infoFieldFooter);
        this.infoFieldFooter = null;
        DestroyUtil.destroy(this.infoFieldFooterLine1);
        this.infoFieldFooterLine1 = null;
        DestroyUtil.destroy(this.infoFieldFooter2);
        this.infoFieldFooter2 = null;
        DestroyUtil.destroy(this.copyright);
        this.copyright = null;
    }
    addEventListeners() {
        super.addEventListeners();
    }
    removeEventListeners() {
        super.removeEventListeners();
    }
    updatePage() { }
    updatePaging() { }
    updateButtons() { }
    createMasks() {
        this.landscapeMask = null;
        this.portraitMask = null;
    }
    createOverlay() {
        this.createInfoFieldSlingo();
        this.createInfoFieldWin();
        if (this.uiProperties.infoFieldFooterProperties) {
            this.infoFieldFooter = TitanText.createField(this, this.landscapeProperties.infoFieldFooterProperties, this.portraitProperties?.infoFieldFooterProperties);
            this.infoFieldFooter.name = "infoField";
            this.infoFieldFooter.setText(`${this.langManager.getLang(GameLang.LOADER_GUIDE_BODY_3_0)} ${this.langManager.getLang(GameLang.LOADER_GUIDE_BODY_3_1)} ${this.langManager.getLang(GameLang.LOADER_GUIDE_BODY_3_2)}`, true);
        }
    }
    createInfoFieldWin() {
        let winStr = this.langManager.getLang(GameLang.LOADER_GUIDE_BODY_0);
        // replace spaces in numbers (eg, on sv-SE "1 000x!") with non-breakin spaces "\u00A0", to keep the number strings together on one line
        // can't use lookahead/lookbehind regexes because they aren't supported in IOS 15 so do it the long way.
        const spacesInNumbers = winStr.match(/\d\s\d/g);
        spacesInNumbers?.forEach(spaceBetweenDigitsInstace => {
            const nonBreakingSpaceBetweenDigitsInstace = spaceBetweenDigitsInstace.replace(" ", "\u00A0");
            winStr = winStr.replace(spaceBetweenDigitsInstace, nonBreakingSpaceBetweenDigitsInstace);
        });
        if ([Locale.ENGLISH, Locale.AMERICAN, Locale.ENGLISH_CANADIAN].includes(this.langManager.getLocale())) {
            // Text with different font styles
            const container = this.addChild(new AbstractGameView(FactoryManager.getInstance().getFactory(), this.landscapeProperties.enLocaleWinContainer, this.portraitProperties?.enLocaleWinContainer));
            const words = winStr.split(' ');
            const last = words.pop();
            const rest = words.join(' ');
            TitanText.createField(container, this.landscapeProperties.enLocaleWinProperties[0]).setText(rest, true);
            TitanText.createField(container, this.landscapeProperties.enLocaleWinProperties[1]).setText(last, true);
        }
        else {
            // Generic text field
            this.infoFieldWin = TitanText.createField(this, this.landscapeProperties.genericWinProperties, this.portraitProperties?.genericWinProperties);
            this.infoFieldWin.name = "infoFieldWin";
            this.infoFieldWin.setText(winStr, true);
        }
    }
    createInfoFieldSlingo() {
        const slingoStr = this.langManager.getLang(GameLang.LOADER_GUIDE_BODY_1);
        // Generic text field
        this.infoFieldSlingo = TitanText.createField(this, this.landscapeProperties.genericSlingoProperties, this.portraitProperties?.genericSlingoProperties);
        this.infoFieldSlingo.name = "infoFieldSlingo";
        this.infoFieldSlingo.setText(slingoStr, true);
    }
    handleRotate(event) {
        super.handleRotate(event);
        const landscape = this.renderer.getOrientation() === OrientationType.LANDSCAPE;
        this.mask = landscape ? this.landscapeMask : this.portraitMask;
        this.landscapeMask && (this.landscapeMask.visible = landscape);
        this.portraitMask && (this.portraitMask.visible = !landscape);
    }
    createProgressBar() {
        if (this.uiProperties.progressBarBackgroundProperties) {
            this.progressBarBackground = new TitanSprite(this.landscapeProperties.progressBarBackgroundProperties, this.portraitProperties?.progressBarBackgroundProperties);
            this.progressBarBackground.name = "progressBarBackground";
            this.addChild(this.progressBarBackground);
        }
        this.progressBar = new TitanSprite(this.landscapeProperties.progressBarProperties, this.portraitProperties?.progressBarProperties);
        if (this.progressBarBackground) {
            this.progressBarBackground.addChild(this.progressBar);
        }
        else {
            this.addChild(this.progressBar);
        }
        this.progressBar.name = "progressBar";
        this.progressBar.position.set(this.uiProperties.progressBarOffsetStart.x, this.uiProperties.progressBarOffsetStart.y);
        if (this.uiProperties.progressBarForegroundProperties) {
            this.progressBarForeground = new TitanSprite(this.landscapeProperties.progressBarForegroundProperties, this.portraitProperties?.progressBarForegroundProperties);
            this.progressBarForeground.name = "progressBarForeground";
            this.addChild(this.progressBarForeground);
        }
    }
    createProgressBarMask() {
        let size = new Point(this.uiProperties.progressBarMaskProperties.width, this.uiProperties.progressBarMaskProperties.height);
        this.progressBarMask = GraphicsUtil.createMask(size);
        this.progressBarMask.name = "progressBarMask";
        this.progressBarMask.position.set(this.uiProperties.progressBarMaskProperties.x, this.uiProperties.progressBarMaskProperties.y);
        this.progressBarForeground.mask = this.progressBarMask;
        if (this.progressBarBackground) {
            this.progressBarBackground.addChild(this.progressBarMask);
        }
        else {
            this.addChild(this.progressBarMask);
        }
        this.updateProgress(0);
    }
    createSwipe() { }
    updateProgress(value) {
        const target = GraphicsUtil.lerpLine([this.uiProperties.progressBarOffsetStart, this.uiProperties.progressBarOffsetEnd], value / 100);
        if (this.progressBarTween) {
            this.progressBarTween.progress(1);
        }
        this.progressBarTween = gsap.to(this.progressBarMask, {
            pixi: {
                x: target.x,
                y: target.y
            },
            duration: 1,
            onUpdate: () => {
                Logger.debug("progress: " + String(Math.round(value)) + "%");
                this.progressField.setText(String(Math.round(value)) + "%");
            }
        });
    }
    completeLoadingProgress() {
        this.dispatchCore(LoaderEvent.PROGRESS_COMPLETE);
        this.playButton.setEnabled(true);
        this.progressBarBackground.visible = false;
        this.progressBarForeground.visible = false;
        this.playButton.renderable = true;
        this.progressField.renderable = false;
    }
    zoom(scale, asset) {
        asset.scale.set(scale);
    }
}
//# sourceMappingURL=GameLoaderDialog.js.map